import { assert, Burden } from '@deqo/foundation';
import React, { ReactNode } from 'react';
import { listen } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { KeyInput } from '../../../platform/input/KeyInput';
import { Modifiers } from '../../../platform/input/Modifiers';
import { MouseButtons, MouseInput } from '../../../platform/input/MouseInput';
import { MultiClickRecognizer } from '../../../platform/input/MultiClickRecognizer';
import { WheelInput } from '../../../platform/input/WheelInput';
import { Fill } from '../../../ui/Fill';
import { Activator, ContainerScope, resolve, Scheduler } from '../../Container';
import { AdapterInfo } from '../AdapterInfo';
import { EditorSession } from '../EditorSession';
import { PointerInfo } from '../PointerInfo';
import { WidgetManager } from '../workspace/widgets/WidgetManager';
import { EdgingHandler } from './EdgingHandler';
import { ToolSettings } from './ToolBehavior';
import { CreateToolInfo, ToolInfo } from './ToolInfo';
import { ToolInstance } from './ToolInstance';
import { ToolManager } from './ToolManager';

export class ToolLayer extends React.Component {

    public static contextType = ContainerScope;

    @resolve(Activator) private activator!: Activator;
    @resolve(CommandDispatcher) private dispatcher!: CommandDispatcher;
    @resolve(EditorSession) private session!: EditorSession;
    @resolve(WidgetManager) private widgets!: WidgetManager;
    @resolve(Scheduler) private scheduler!: Scheduler;
    @resolve(ToolManager) private tools!: ToolManager;

    private trash = [] as Burden[];
    private layerElmt = React.createRef<HTMLDivElement>();
    private downMcr = new MultiClickRecognizer();
    private upMcr = new MultiClickRecognizer();

    private instance = null as ToolInstance | null;

    public render() {
        return <>
            <EdgingHandler onEdge={this.onEdge} />
            <Fill
                id="tool-layer"
                ref={this.layerElmt}
                onMouseDown={this.onMouse}
                onMouseMove={this.onMouse}
                // onMouseUp={this.onMouse} See below
                onClick={this.onMouse}
                onDoubleClick={this.onMouse}
                onContextMenu={this.onMenu}
                tabIndex={0}
            />
        </>;
    }

    public componentDidMount() {
        this.trash.push(this.scheduler);
        // Passive events are not supported by React so we have to do this the old fashioned way...
        this.trash.push(listen(document.body, 'wheel', this.onWheel, { passive: false }));
        // Handle mouse up specifically to handle clicking off onto a UI element and not getting events caught...
        this.trash.push(listen(document.body, 'mouseup', this.onMouseUp));
        // May as well do these this way too
        this.trash.push(listen(document.body, 'keydown', this.onKey));
        this.trash.push(listen(document.body, 'keyup', this.onKey));
        this.trash.push(this.session.observe(ToolInfo, x => this.equipTool(x)));
        this.equipTool(this.session.get(ToolInfo));
        this.layerElmt.current.focus();
    }

    public componentWillUnmount() {
        this.instance?.destroy();
        Burden.resolveAll(this.trash);
        this.layerElmt.current.removeEventListener('wheel', this.onWheel);
    }

    private wrapHandler<T extends (...args: any[]) => any>(func: T): T {
        const wrapped = (...args: any[]): any => {
            try {
                return func(...args);
            }
            catch (err) {
                this.resetTool();
                throw err;
            }
        };
        return wrapped as any;
    }

    private onEdge = this.wrapHandler(() => {
        const input = this.createSyntheticMouseInput(MouseButtons.None);
        this.instance?.dispatchEvent('edge', input);
    });

    private onMouse = this.wrapHandler((e: any) => {
        if (e.type === 'mousedown') {
            e.preventDefault();
            this.layerElmt.current.focus();
        }
        const input = this.createMouseInput(e);
        this.instance?.dispatchEvent(e.type, input);
    });

    private onMouseUp = this.wrapHandler((e: any) => {
        this.onMouse(e);
    });

    private onMenu = this.wrapHandler((e: any) => {
        if (!e.ctrlKey) {
            e.preventDefault();
        }
    });

    private onKey = this.wrapHandler((e: any) => {
        // As "key" events are on the document we need to check whether it is applicable to fire the event action
        if (!this.shouldFireKeyAction(this.layerElmt.current, e)) return;
        // If there is an active adapter, don't fire key events
        if (this.session.get(AdapterInfo)?.target !== 'none') return;
        const input = KeyInput.fromEvent(e, this.getPointerTargets());
        if (this.instance?.dispatchEvent(e.type, input)) {
            e.preventDefault();
        }
    });

    private onWheel = this.wrapHandler((e: any) => {
        // As "wheel" events are on the document we need to check whether it is applicable to fire the event action
        if (!this.shouldFireWheelAction(this.layerElmt.current, e)) return;
        const input = WheelInput.fromEvent(e, this.layerElmt.current);
        e.preventDefault();
        if (this.instance?.dispatchEvent('wheel', input)) {
            e.preventDefault();
        }
    });

    private equipTool = (next: ToolInfo, force?: boolean) => {
        const instance = this.instance;
        const nextSettings = this.tools.getSettings(next.id);

        // Only equip if no instance or instance id/param has changed
        const hasChanged = (
            !instance ||
            instance.id !== next.id ||
            instance.param !== next.param ||
            instance.settings !== nextSettings
        );

        if (hasChanged || force) {

            instance?.destroy();

            this.instance = this.createInstanceFor(next, nextSettings);
            this.scheduler.invokeImmedate(() => this.instance?.init());
        }
    };

    private resetTool = () => {
        if (!this.instance) return;
        // const info = CreateToolInfo(instance.id, 'idle', instance.param);
        const info = CreateToolInfo('default', 'idle');
        this.equipTool(info, true);
    };

    private createInstanceFor(next: ToolInfo, nextSettings: ToolSettings) {
        const { activator, session, tools } = this;

        const toolDef = tools.get(next.id);

        assert(!!toolDef, `ToolInstance: could not find definition for ${next.id}`);
        assert(!!toolDef.behaviors, `ToolInstance: ${next.id} looks like a command tool?`);

        const compiledBehaviors = toolDef?.behaviors.map(activator.call) || [];

        const instance = new ToolInstance(
            next,
            nextSettings,
            compiledBehaviors,
            this.dispatcher,
            state => session.patch(ToolInfo, { state }),
        );

        return instance;
    }

    private createMouseInput(e: MouseEvent) {
        const position = MouseInput.computePosition(e, this.layerElmt.current);
        const count = this.getClickCount(e);
        const targets = this.getPointerTargets();
        return new MouseInput(
            e.button,
            Modifiers.fromEvent(e),
            position,
            count,
            targets === undefined ? [e.target] : targets,
        );
    }

    private createSyntheticMouseInput(button: MouseButtons) {
        const { position, targets } = this.session.get(PointerInfo);
        return new MouseInput(
            button,
            Modifiers.fromValues(false, false, false),
            position,
            1,
            targets,
        );
    }

    private getClickCount(e: MouseEvent) {
        if (e.type === 'mousedown') return this.downMcr.next(e);
        if (e.type === 'mouseup') return this.upMcr.next(e);
        return 0;
    }

    private getPointerTargets = () => this.session.get(PointerInfo).targets;

    private shouldFireKeyAction = (layer: HTMLElement, { target }: any) => {
        // As "key" events are on the document we need to check no random ui is the target of the key event...
        // If the target is body OR target is layer or child of layer, fire action.
        if (target === document.body) return true;
        while (target) {
            if (target && target === layer) return true;
            target = target.parentElement;
        }
        return false;
    };

    private shouldFireWheelAction = (layer: HTMLElement, { target }: any) => {
        // If the target is layer or child of layer, fire action...
        while (target) {
            if (target && target === layer) return true;
            if (target && target.classList.contains('captureWheel')) return true;
            if (target && target.classList.contains('blockWheel')) return false;
            target = target.parentElement;
        }
        return false;
    };
}

export const BlockWheel = ({ children }: { children?: ReactNode }) => {
    return (
        <div className="blockWheel">
            {children}
        </div>
    );
};

export const CaptureWheel = ({ children }: { children?: ReactNode }) => {
    return (
        <div className="captureWheel">
            {children}
        </div>
    );
};
